import React, { useEffect, useState } from 'react';
import { db } from '../../utils/firebase';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { Product } from '../../types';
import { isVaultItem } from '../../utils/productWindows';
import VaultProductCard from './VaultProductCard';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

const VaultPage: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  // Header content — CMS-overridable via page_content/vault.
  // Default is the plain black band; an image renders dimly behind it.
  const [pageContent, setPageContent] = useState({
    title: 'THE VAULT',
    subtitle: 'Closed. Held, not sold.',
    heroImage: ''
  });

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const snap = await getDoc(doc(db, 'page_content', 'vault'));
        if (snap.exists()) {
          const data = snap.data();
          setPageContent(prev => ({
            title: data.title || prev.title,
            subtitle: data.subtitle || prev.subtitle,
            heroImage: data.heroImage || ''
          }));
        }
      } catch {
        // Keep the plain black band silently
      }
    };
    fetchPageContent();
  }, []);

  useEffect(() => {
    const fetchVaultProducts = async () => {
      try {
        // Vault membership = closed window. A fragment whose windowCloseAt has
        // passed appears here automatically. Legacy sold_out items are kept
        // for continuity with the existing vault.
        const [fragmentsSnap, legacySnap] = await Promise.all([
          getDocs(query(collection(db, 'products'), where('tier', '==', 'fragment'))),
          getDocs(query(
            collection(db, 'products'),
            where('status', '==', 'sold_out'),
            orderBy('createdAt', 'desc')
          ))
        ]);

        const byId = new Map<string, Product>();
        const now = new Date();

        fragmentsSnap.forEach((doc) => {
          const product = { id: doc.id, ...doc.data() } as Product;
          if (isVaultItem(product, now)) {
            byId.set(product.id, product);
          }
        });

        legacySnap.forEach((doc) => {
          const product = { id: doc.id, ...doc.data() } as Product;
          // Claimed artifacts are 'claimed', not vaulted
          if (product.tier === 'artifact') return;
          if (!byId.has(product.id)) {
            byId.set(product.id, product);
          }
        });

        // Most recently closed / created first
        const productList = Array.from(byId.values()).sort((a, b) => {
          const aKey = a.windowCloseAt || '';
          const bKey = b.windowCloseAt || '';
          return bKey.localeCompare(aKey);
        });

        setProducts(productList);
      } catch (error) {
        console.error("Error fetching vault products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVaultProducts();
  }, []);

  return (
    <div className="pt-24 min-h-screen bg-white">
      {/* Header — the darkest room in the building */}
      <div className="relative bg-black text-white py-16 px-6 mb-12 overflow-hidden">
        {pageContent.heroImage && (
          <img
            src={pageContent.heroImage}
            alt=""
            aria-hidden="true"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
        )}
        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <h1 className="font-display font-normal text-5xl md:text-7xl mb-4 tracking-wide">{pageContent.title}</h1>
          <p className="font-mono text-gray-400 max-w-2xl mx-auto tracking-widest text-sm">
            {pageContent.subtitle}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 pb-24">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="animate-spin text-gray-400" size={32} />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
              {products.map((product) => (
                <VaultProductCard 
                  key={product.id} 
                  product={product}
                  onClick={() => navigate(`/vault/${product.id}`)}
                />
              ))}
            </div>

            {products.length === 0 && (
              <div className="text-center py-20">
                <p className="font-mono text-gray-400">The vault is currently empty.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VaultPage;
